import { useState, useCallback, useEffect } from 'react'

import axios from 'axios'

import { normalizeUseCasesResponse } from 'Pods/LazyLoaded/UseCaseAreaSidebar/Utils/Data'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'

import isAxiosCancelOrNetworkError from 'Utils/isAxiosCancelOrNetworkError'

const selectProductId = (config) => config?.product?.id

const useItemUseCases = (modelId = null) => {
  const productId = usePageConfig({ select: selectProductId })
  const { captureException } = useSentry()

  const [useCases, setUseCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const addUseCaseOptimistic = useCallback((tempUseCase) => {
    setUseCases(prev => [...prev, tempUseCase])
  }, [])

  const removeUseCaseOptimistic = useCallback((tempId) => {
    setUseCases(prev => prev.filter(uc => uc.id !== tempId))
  }, [])

  const updateUseCaseOptimistic = useCallback((useCaseId, updates) => {
    setUseCases(prev => prev.map(uc =>
      uc.id === useCaseId ? { ...uc, ...updates } : uc
    ))
  }, [])

  const fetchUseCases = useCallback((itemId, abortSignal) => {
    const config = {
      withCredentials: true,
      signal: abortSignal,
    }

    return axios.get(`/api/internal/items/${itemId}/item_use_cases`, config)
      .then((response) => {
        // axios resolves a status of 0 - aborted, blocked by an extension, or a failed
        // connection - with an empty body, because settle() treats a falsy status as
        // success before validateStatus is consulted (MARKETPLACE-JS-8KND). Nothing was
        // delivered, so the body must not be normalized.
        if (!response.status) {
          // We aborted it ourselves and a newer request is already in flight; that one
          // owns the state. Any other status 0 is a dead end, so stop loading instead of
          // leaving consumers on a spinner forever. It reflects the visitor's connection
          // rather than an application fault, so it is not reported.
          if (abortSignal?.aborted) return

          setError(new Error('Use cases request did not complete'))
          setLoading(false)

          return
        }

        setUseCases(normalizeUseCasesResponse(response.data))
        setLoading(false)
        setError(null)
      })
      .catch((err) => {
        if (!isAxiosCancelOrNetworkError(err)) {
          captureException(err)
          setError(err)
          setLoading(false)
        }
      })
  }, [captureException])

  const refetch = useCallback(() => {
    const itemId = productId || modelId
    if (!itemId) return

    setLoading(true)
    setError(null)
    fetchUseCases(itemId)
  }, [productId, modelId, fetchUseCases])

  useEffect(() => {
    const itemId = productId || modelId
    if (!itemId) return

    setError(null)

    const abortController = new AbortController()

    fetchUseCases(itemId, abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [productId, modelId, fetchUseCases])

  return {
    useCases,
    loading,
    error,
    addUseCaseOptimistic,
    removeUseCaseOptimistic,
    updateUseCaseOptimistic,
    refetch,
  }
}

export default useItemUseCases
